import { loyaltyApi } from '@/plugins/http-axios';

const state = {
    balance: {
        loyaltyPoints: 0,
        totalEarnedPoints: 0
    },
    transactions: [],
    referralCode: null,
    referralCodeUsageCount: 0,
    referralCodeUsageLimit: null,
    loading: false,
    error: null,
    lastUpdate: null
};

const getters = {
    getLoyaltyPoints: (state) => state.balance.loyaltyPoints,
    getTotalEarnedPoints: (state) => state.balance.totalEarnedPoints,
    getTransactions: (state) => state.transactions,
    getReferralCode: (state) => state.referralCode,
    getReferralCodeUsageCount: (state) => state.referralCodeUsageCount,
    getReferralCodeUsageLimit: (state) => state.referralCodeUsageLimit,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    hasPoints: (state) => state.balance.loyaltyPoints > 0
};

const mutations = {
    SET_BALANCE(state, balance) {
        state.balance = balance;
        state.lastUpdate = new Date();
    },
    SET_TRANSACTIONS(state, transactions) {
        state.transactions = transactions;
    },
    SET_REFERRAL_CODE(state, referralData) {
        state.referralCode = referralData.referralCode;
        state.referralCodeUsageCount = referralData.usageCount || 0;
        state.referralCodeUsageLimit = referralData.usageLimit;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    CLEAR_ERROR(state) {
        state.error = null;
    },
    RESET_STATE(state) {
        state.balance = { loyaltyPoints: 0, totalEarnedPoints: 0 };
        state.transactions = [];
        state.referralCode = null;
        state.referralCodeUsageCount = 0;
        state.referralCodeUsageLimit = null;
        state.loading = false;
        state.error = null;
        state.lastUpdate = null;
    }
};

const actions = {
    async fetchBalance({ commit }) {
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');
        try {
            const response = await loyaltyApi.getBalance();
            commit('SET_BALANCE', response.data);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Ошибка при загрузке баланса';
            commit('SET_ERROR', errorMessage);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async fetchTransactions({ commit }) {
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');
        try {
            const response = await loyaltyApi.getTransactions();
            const transactions = response.data.loyaltyTransactions || [];
            commit('SET_TRANSACTIONS', transactions);
            // Также обновляем баланс из полного ответа
            if (response.data.loyaltyPoints !== undefined) {
                commit('SET_BALANCE', {
                    loyaltyPoints: response.data.loyaltyPoints,
                    totalEarnedPoints: response.data.totalEarnedPoints
                });
            }
            return transactions;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Ошибка при загрузке истории';
            commit('SET_ERROR', errorMessage);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async redeemCode({ commit, dispatch }, code) {
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');
        try {
            const response = await loyaltyApi.redeemCode(code);
            // Обновляем баланс и транзакции после активации
            await dispatch('fetchBalance');
            await dispatch('fetchTransactions');
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Ошибка при активации промокода';
            commit('SET_ERROR', errorMessage);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async getReferralCode({ commit }) {
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');
        try {
            const response = await loyaltyApi.getReferralCode();
            commit('SET_REFERRAL_CODE', response.data);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Ошибка при загрузке реферального кода';
            commit('SET_ERROR', errorMessage);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async useReferralCode({ commit, dispatch }, referralCode) {
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');
        try {
            const response = await loyaltyApi.useReferralCode(referralCode);
            // Обновляем баланс и транзакции после использования реферального кода
            await dispatch('fetchBalance');
            await dispatch('fetchTransactions');
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Ошибка при использовании реферального кода';
            commit('SET_ERROR', errorMessage);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    clearError({ commit }) {
        commit('CLEAR_ERROR');
    },

    reset({ commit }) {
        commit('RESET_STATE');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};

